import { Ability } from '@casl/ability';
import {
  Actions,
  ResultStatus,
  Subjects,
  TaskAssignedResource,
  TaskResponse,
} from '@dispatch/shared/types';
import { ConfirmModal } from '@dispatch/web/ui';
import { Button, Stack } from '@mui/material';
import { isBefore } from 'date-fns';
import { isNull } from 'lodash';
import React from 'react';

type UseMarkUnassignProps = {
  data?: TaskResponse;
  isAdjuster?: boolean;
  permissions?: Ability<[Actions, Subjects]>;
  handleUnassignTask?: (taskId: string) => void;
  isCancelled?: boolean;
  isLoading?: boolean;
  showCancel?: boolean;
  cancelStatus?: ResultStatus;
  canCancelClaimOwner: boolean;
};

export const useMarkUnassign = ({
  data,
  isAdjuster,
  permissions,
  handleUnassignTask,
  isCancelled,
  isLoading,
  showCancel = true,
  cancelStatus,
  canCancelClaimOwner = false,
}: UseMarkUnassignProps) => {
  const [showUnassignedTaskModal, setShowUnassignedTaskModal] =
    React.useState(false);

  const handleUnassignedTaskModal = () => {
    setShowUnassignedTaskModal(true);
  };

  const renderUnassignedTaskButton = React.useMemo(
    () => (
      <Stack px={1}>
        <Button
          data-testid="unassigned-task-button"
          type="button"
          variant="outlined"
          color="error"
          onClick={handleUnassignedTaskModal}
          disabled={!showCancel || isCancelled || isLoading}
          sx={{
            fontWeight: '700',
            lineHeight: '1.3750em',
            letterSpacing: '0.029em',
          }}
        >
          Set Task to Unassigned
        </Button>
      </Stack>
    ),

    [showUnassignedTaskModal, isCancelled, isLoading]
  );

  const canUnassignFieldTask = React.useMemo(() => {
    const canEditTasks = permissions?.can('update', 'task');

    return canEditTasks;
  }, [permissions]);

  const canUnassignComplexTask = React.useMemo(() => {
    const canEditTasks = permissions?.can('upsert', 'complex-loss');

    return canEditTasks;
  }, [permissions]);

  const unassignedButtonValidation = (
    isAdjuster: boolean | undefined,
    lockedAt: string | null | undefined,
    canUnassignTask?: boolean,
    taskType?: string,
    assignedResource?: TaskAssignedResource | null | undefined
  ) => {
    const datetoCheck = new Date(
      data?.$msg?.assignmentHandling?.appointment ?? ''
    );

    if (isAdjuster) return false;
    if (!isNull(lockedAt)) return false;
    if (isNull(assignedResource)) return false;
    if (
      ['CLAIM_HANDLER', 'NON_COMPLEX', 'CLAIM_OWNER'].includes(
        taskType || ''
      ) &&
      !canUnassignTask
    )
      return false;
    if (taskType && ['COMPLEX'].includes(taskType) && !canUnassignComplexTask)
      return false;

    if (
      taskType &&
      !['CLAIM_OWNER', 'COMPLEX'].includes(taskType) &&
      isBefore(datetoCheck, new Date())
    )
      return false;

    return true;
  };

  const setUnassignedTaskButton = React.useMemo(() => {
    if (
      !unassignedButtonValidation(
        isAdjuster,
        data?.$msg?.lockedAt,
        canUnassignFieldTask,
        data?.$msg?.taskType,
        data?.$msg?.assignedResource
      )
    )
      return [];

    if (data?.$msg?.taskType === 'CLAIM_OWNER') {
      if (canCancelClaimOwner) {
        return renderUnassignedTaskButton;
      }

      return [];
    }

    return renderUnassignedTaskButton;
  }, [
    data?.$msg?.assignedResource,
    data?.$msg.assignmentHandling.appointment,
    data?.$msg?.taskType,
    data?.$msg?.lockedAt,
    isAdjuster,
    canUnassignFieldTask,
    canCancelClaimOwner,
    renderUnassignedTaskButton,
  ]);

  const renderUnassignedModal = () => {
    return (
      <ConfirmModal
        actionStatus={cancelStatus}
        onConfirm={() => {
          handleUnassignTask?.(data?.id || '');
          setShowUnassignedTaskModal(false);
        }}
        onDeny={() => {
          setShowUnassignedTaskModal(false);
        }}
        confirmContent={
          <p>Do you really want to set this task to unassigned?</p>
        }
        confirmTitle={'Set Task to Unassigned'}
        confirmButtonLabel="Confirm"
      />
    );
  };

  return {
    showUnassignedTaskModal,
    setShowUnassignedTaskModal,
    renderUnassignedModal,
    setUnassignedTaskButton,
  };
};
